import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { IconCheck, IconCopy, IconMail } from "@tabler/icons-react";
import { checkMail } from "../../utils/functions";
import { submitRoiResult, submitRoiResultFallback } from "../../services/roiLead";
import { getFormUtm } from "../../utils/attribution";
import {
  ROI_LIMITS,
  formatHours,
  readRoiFromSearch,
  readUtm,
  roiResult,
  shareSearch,
} from "./roiMath";
import styles from "./RoiCalculator.module.css";

const trackResultEmail = () => {
  try {
    if (typeof window.gtag === "function") {
      window.gtag("event", "generate_lead", { method: "roi_calculator" });
    }
    if (typeof window.fbq === "function") {
      window.fbq("track", "Lead");
    }
  } catch (_) {
    // Tracking must not block the result email.
  }
};

const buildShareUrl = (values) => {
  const query = shareSearch(values, window.location.search);
  return `${window.location.origin}/api-integrations?${query}#time-saved`;
};

// Prefer the campaign stored for this visit (includes ad click ids). Fill any
// gaps from the URL so a lead is still tagged if storage is blocked.
const campaignUtm = (search) => {
  const utm = { ...readUtm(search) };
  const stored = getFormUtm().utm || {};
  ["source", "medium", "campaign", "content", "term"].forEach((key) => {
    if (stored[key]) utm[key] = stored[key];
  });
  return utm;
};

export default function RoiCalculator() {
  const initial = useMemo(() => readRoiFromSearch(window.location.search), []);
  const [applicants, setApplicants] = useState(initial.applicants);
  const [minutes, setMinutes] = useState(initial.minutes);
  const [roles, setRoles] = useState(initial.roles);
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [copied, setCopied] = useState(false);
  const [sending, setSending] = useState(false);
  const [sentTo, setSentTo] = useState("");
  const [formError, setFormError] = useState("");

  const values = { applicants, minutes, roles };
  const result = roiResult(values);
  const perRole = formatHours(result.hoursPerRole);
  const perApplicant = formatHours(result.hoursPerApplicant);
  const perYear = formatHours(result.hoursPerYear);

  useEffect(() => {
    if (!initial.hasAny && window.location.hash !== "#time-saved") return undefined;
    const timer = window.setTimeout(() => {
      document.getElementById("time-saved")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
    return () => window.clearTimeout(timer);
  }, [initial.hasAny]);

  const publishShareUrl = () => {
    const url = buildShareUrl(values);
    const query = url.slice(url.indexOf("?"));
    window.history.replaceState(null, "", query);
    return url;
  };

  const copyLink = async () => {
    const url = publishShareUrl();
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      setCopied(false);
      setFormError("Copy the link from the address bar — clipboard access was blocked.");
    }
  };

  const sendResult = async (event) => {
    event.preventDefault();
    const trimmed = email.trim();
    if (!checkMail(trimmed)) {
      setFormError("Enter a valid work email.");
      return;
    }
    setFormError("");
    setSending(true);
    const landingPage = buildShareUrl(values);
    const payload = {
      email: trimmed,
      applicants,
      minutes,
      roles,
      marketingConsent: consent,
      landingPage,
      utm: campaignUtm(window.location.search),
    };
    try {
      const response = await submitRoiResult(payload);
      publishShareUrl();
      trackResultEmail();
      if (response?.data?.data?.source === "roi_calculator") {
        setSentTo(trimmed);
        return;
      }
      setFormError("We saved your email. The inbox copy needs the latest API — use the share link in the meantime.");
    } catch (_) {
      try {
        await submitRoiResultFallback(payload);
        publishShareUrl();
        trackResultEmail();
        setFormError("We saved your email. The inbox copy needs the latest API — use the share link in the meantime.");
      } catch (__) {
        setFormError("Couldn't send that just now. Copy the share link and try the email again.");
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <div className={styles.layout}>
      <div className={styles.controls}>
        <Slider
          id="roi-applicants"
          label="Applicants per role"
          value={applicants}
          limits={ROI_LIMITS.applicants}
          display={String(applicants)}
          onChange={setApplicants}
        />
        <Slider
          id="roi-minutes"
          label="Hours spent screening each applicant"
          value={minutes}
          limits={ROI_LIMITS.minutes}
          display={`${minutes} min · ${perApplicant.label}`}
          onChange={setMinutes}
        />
        <Slider
          id="roi-roles"
          label="Roles you open each month"
          value={roles}
          limits={ROI_LIMITS.roles}
          display={String(roles)}
          onChange={setRoles}
        />
        <p className={styles.formula} aria-live="polite">
          <span>{applicants} applicants</span>
          <span className={styles.op}>×</span>
          <span>{perApplicant.label}</span>
          <span className={styles.op}>=</span>
          <strong>{perRole.label} saved per role</strong>
        </p>
      </div>

      <div className={styles.result}>
        <div className={styles.resultKicker}>Time saved</div>
        <div className={styles.big}>{perRole.text}</div>
        <div className={styles.bigUnit}>{perRole.unit} saved per role</div>
        <p className={styles.year}>
          {roles} {roles === 1 ? "role" : "roles"} a month is {perYear.label} a year you don't spend screening by hand.
        </p>
        <p className={styles.assumption}>
          Uses the time you entered for each applicant. That's the time you get back if that screening no longer happens manually.
        </p>

        <button type="button" className={styles.shareBtn} onClick={copyLink}>
          {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
          {copied ? "Link copied" : "Copy share link"}
        </button>

        {sentTo ? (
          <p className={styles.sent} role="status">
            Sent. The breakdown is on its way to {sentTo}.
          </p>
        ) : (
          <form className={styles.emailForm} onSubmit={sendResult}>
            <label htmlFor="roi-email">Email me this result</label>
            <div className={styles.emailRow}>
              <input
                id="roi-email"
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="you@company.com"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
              <button type="submit" disabled={sending}>
                <IconMail size={16} />
                {sending ? "Sending…" : "Send"}
              </button>
            </div>
            <label className={styles.consent}>
              <input
                type="checkbox"
                checked={consent}
                onChange={(event) => setConsent(event.target.checked)}
              />
              <span>
                Also send occasional product updates. Leave this unticked and we only email this result.{" "}
                <Link to="/privacy-policy">Privacy policy</Link>
              </span>
            </label>
          </form>
        )}
        {formError ? <p className={styles.error}>{formError}</p> : null}
      </div>
    </div>
  );
}

function Slider({ id, label, value, limits, display, onChange }) {
  return (
    <div className={styles.field}>
      <div className={styles.fieldHead}>
        <label htmlFor={id}>{label}</label>
        <output htmlFor={id}>{display}</output>
      </div>
      <input
        id={id}
        type="range"
        min={limits.min}
        max={limits.max}
        step={limits.step}
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
      />
      <div className={styles.scale}>
        <span>{limits.min}</span>
        <span>{limits.max}</span>
      </div>
    </div>
  );
}
